import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  flag: string;
  rate: number; // Exchange rate relative to EGP
}

@Injectable({
  providedIn: 'root',
})
export class CurrencyService {
  private readonly STORAGE_KEY = 'selected_currency';
  private readonly COUNTRY_STORAGE_KEY = 'selected_country';
  private readonly DEFAULT_CURRENCY = 'EGP';

  private currencies: Currency[] = [
    { code: 'EGP', symbol: 'ج.م', name: 'Egyptian Pound', flag: 'eg', rate: 1 },
    { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal', flag: 'sa', rate: 0.12 },
    { code: 'USD', symbol: '$', name: 'US Dollar', flag: 'us', rate: 0.032 },
  ];

  private currentCurrencySubject: BehaviorSubject<string>;
  public currentCurrency$: Observable<string>;

  constructor() {
    const savedCurrency = this.getSavedCurrency();
    this.currentCurrencySubject = new BehaviorSubject<string>(savedCurrency);
    this.currentCurrency$ = this.currentCurrencySubject.asObservable();
  }

  private getSavedCurrency(): string {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(this.STORAGE_KEY) || this.DEFAULT_CURRENCY;
    }
    return this.DEFAULT_CURRENCY;
  }

  setCurrency(currencyCode: string): void {
    if (this.currencies.find((c) => c.code === currencyCode)) {
      this.currentCurrencySubject.next(currencyCode);
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(this.STORAGE_KEY, currencyCode);
      }
    }
  }

  setCountry(country: string): void {
    if (!country || typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    localStorage.setItem(this.COUNTRY_STORAGE_KEY, country);
  }

  getCountry(): string | null {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return localStorage.getItem(this.COUNTRY_STORAGE_KEY);
  }

  setCurrencyByCountry(country: string): void {
    if (!country) {
      return;
    }

    const normalizedCountry = country.trim().toLowerCase();
    const countryToCurrencyMap: Record<string, string> = {
      egypt: 'EGP',
      'saudi arabia': 'SAR',
      // 'united arab emirates': 'USD',
      // kuwait: 'USD',
      // qatar: 'USD',
      // bahrain: 'USD',
      // oman: 'USD',
      // usa: 'USD',
      // 'united states': 'USD',
    };

    const currencyCode = countryToCurrencyMap[normalizedCountry] || this.DEFAULT_CURRENCY;
    this.setCountry(country);
    this.setCurrency(currencyCode);
  }

  getCurrentCurrency(): string {
    return this.currentCurrencySubject.value;
  }

  getCurrencyInfo(code: string): Currency | undefined {
    return this.currencies.find((c) => c.code === code);
  }

  getAllCurrencies(): Currency[] {
    return this.currencies;
  }

  convertPrice(amount: number, fromCurrency: string = 'EGP', toCurrency?: string): number {
    const targetCurrency = toCurrency || this.getCurrentCurrency();

    if (fromCurrency === targetCurrency) {
      return amount;
    }

    const fromRate = this.getCurrencyInfo(fromCurrency)?.rate || 1;
    const toRate = this.getCurrencyInfo(targetCurrency)?.rate || 1;

    // Convert to EGP first, then to target currency
    const amountInEGP = amount / fromRate;
    return amountInEGP * toRate;
  }

  formatPrice(amount: number, currencyCode?: string): string {
    const currency = this.getCurrencyInfo(currencyCode || this.getCurrentCurrency());
    if (!currency) {
      return amount.toLocaleString();
    }

    const convertedAmount = this.convertPrice(amount, 'EGP', currency.code);
    return `${convertedAmount.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} ${currency.symbol}`;
  }
}
